import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { SlotService, type ManualBlockingHistory } from "@/lib/services/slotService";
import { 
  Calendar, 
  Clock, 
  ArrowLeft,
  Ban,
  Unlock,
  Download,
  FileText,
  BarChart3,
  TrendingUp,
  Shield,
  AlertTriangle,
  Info
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";

const ManualBlockingHistoryPage = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [blockedSlots, setBlockedSlots] = useState<ManualBlockingHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState<string>("");
  const [stats, setStats] = useState({
    totalBlocked: 0,
    uniqueDates: 0,
    topReasons: [] as Array<{ reason: string; count: number }>
  });

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || user?.type !== "admin") {
      navigate("/admin-dashboard");
      return;
    }

    loadManualBlockingHistory();
    loadStats();
  }, [isAuthenticated, user, navigate, isLoading]);

  const loadManualBlockingHistory = async () => {
    setLoading(true);
    try {
      const history = await SlotService.getManuallyBlockedSlots();
      setBlockedSlots(history);
    } catch (error) {
      console.error("Error loading manual blocking history:", error);
      toast({
        title: "Error",
        description: "Failed to load manual blocking history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statistics = await SlotService.getManualBlockingStats();
      setStats(statistics);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleUnblockSlot = async (slotId: string) => {
    try {
      const success = await SlotService.unblockSlots([slotId]);

      if (success) {
        toast({
          title: "Slot Unblocked",
          description: "The slot has been unblocked and is now available for booking.",
        });
        loadManualBlockingHistory();
        loadStats();
      } else {
        toast({
          title: "Unblock Failed",
          description: "Failed to unblock slot. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error unblocking slot:", error);
      toast({
        title: "Error",
        description: "An error occurred while unblocking the slot.",
        variant: "destructive",
      });
    }
  };

  const exportToExcel = (data: ManualBlockingHistory[], fileName: string) => {
    const formattedData = data.map((slot) => ({
      Date: slot.date,
      "Time Slot": formatTimeSlot(slot.time_start, slot.time_end),
      "Block Reason": slot.block_reason || "No reason provided",
      Status: slot.status.charAt(0).toUpperCase() + slot.status.slice(1),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Manual Blocking History");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const filteredSlots = blockedSlots.filter((slot) => {
    if (!filterDate) return true;
    return slot.date === filterDate;
  });

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      const hour = Number.parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`;
  };

  const groupSlotsByDate = (slots: ManualBlockingHistory[]) => {
    const grouped = slots.reduce((acc, slot) => {
      if (!acc[slot.date]) {
        acc[slot.date] = [];
      }
      acc[slot.date].push(slot);
      return acc;
    }, {} as Record<string, ManualBlockingHistory[]>);

    // Sort slots within each date
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time_start.localeCompare(b.time_start));
    });

    return grouped;
  };

  const groupSlotsByReason = (slots: ManualBlockingHistory[]) => {
    return slots.reduce((acc, slot) => {
      const reason = slot.block_reason || "No reason provided";
      if (!acc[reason]) {
        acc[reason] = [];
      }
      acc[reason].push(slot);
      return acc;
    }, {} as Record<string, ManualBlockingHistory[]>);
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading manual blocking history...</p>
        </div>
      </div>
    );
  }

  const groupedByDate = groupSlotsByDate(filteredSlots);
  const groupedByReason = groupSlotsByReason(filteredSlots);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin-dashboard")}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Manual Blocking History</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Manually Blocked Slots</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="filter-date" className="whitespace-nowrap">
                Filter by Date:
              </Label>
              <Input
                id="filter-date"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="max-w-[180px]"
              />
              {filterDate && (
                <Button
                  variant="ghost"
                  onClick={() => setFilterDate("")}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => exportToExcel(filteredSlots, "manual_blocking_history")}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.totalBlocked}</p>
                  <p className="text-sm text-gray-600">Total Blocked Slots</p>
                </div>
                <Ban className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-orange-600">{stats.uniqueDates}</p>
                  <p className="text-sm text-gray-600">Affected Dates</p>
                </div>
                <Calendar className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">{Object.keys(groupedByReason).length}</p>
                  <p className="text-sm text-gray-600">Unique Reasons</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{filteredSlots.length}</p>
                  <p className="text-sm text-gray-600">Filtered Results</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Reasons Card */}
        {stats.topReasons.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Top Blocking Reasons
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.topReasons.map((reason, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 truncate" title={reason.reason}>
                        {reason.reason}
                      </div>
                      <div className="text-sm text-gray-600">{reason.count} slots</div>
                    </div>
                    <Badge variant="secondary">{index + 1}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {filteredSlots.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Ban className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Manually Blocked Slots Found</h3>
              <p className="text-gray-600">
                {filterDate 
                  ? "No manually blocked slots found for the selected date." 
                  : "No slots have been manually blocked yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Group by Date */}
            {Object.entries(groupedByDate)
              .sort(([a], [b]) => b.localeCompare(a)) // Sort dates descending
              .map(([date, slots]) => (
                <Card key={date} className="border-l-4 border-l-red-500">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 mr-2" />
                        {date}
                      </div>
                      <Badge variant="destructive">{slots.length} blocked slots</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {slots.map((slot) => (
                        <div
                          key={slot.id}
                          className="border rounded-lg p-4 bg-red-50 border-red-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-red-600" />
                              <span className="font-medium text-gray-900">
                                {formatTimeSlot(slot.time_start, slot.time_end)}
                              </span>
                            </div>
                            <Badge variant="destructive" className="text-xs">
                              Blocked
                            </Badge>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="text-sm">
                              <span className="font-medium text-gray-700">Reason:</span>
                              <div className="text-gray-600 mt-1 p-2 bg-white rounded border">
                                {slot.block_reason || "No reason provided"}
                              </div>
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={() => handleUnblockSlot(slot.id)}
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <Unlock className="w-4 h-4 mr-2" />
                              Unblock Slot
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            }
            
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Note:</strong> Unblocking slots will make them available for user bookings again. 
                This action cannot be undone, but slots can be manually blocked again if needed.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManualBlockingHistoryPage;